import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { FaEllipsisH, FaMagic, FaStop, FaChevronLeft, FaChevronRight, FaCog, FaPalette, FaPaperclip, FaImage, FaPaperPlane, FaChevronDown, FaUser, FaPersonBooth, FaUserAlt, FaUserSecret, FaUserEdit, FaWifi, FaSignal, FaCalendarAlt, FaStopwatch, FaTshirt, FaSpinner } from "react-icons/fa";
import { FaQuestionCircle } from "react-icons/fa";
import toast from "react-hot-toast";
// Sample images for modal cards
import videoOptionAnimate from "../assets/creationoption-thumbnails/video_animate.gif";
// import videoOptionChangeposeGif from "../assets/creationoption-thumbnails/change_pose.gif";

import VideoOptionsModal from "./pills/VideoOptionsModal";
import ReferenceImageModal from "./pills/ReferenceImageModal";
import TagProductModal1 from "./pills/TagProductModal1";
import AspectRatioModal from "./pills/AspectRatioModal";
import VideoResolutionModal from "./pills/VideoResolutionModal";
import VideoModeModal from "./pills/VideoModeModal";
import OutputSamplesModal from "./pills/OutputSamplesModal";
import VideoDurationModal from "./pills/VideoDurationModal";
import AIModelSelector from "./pills/AIModelSelector.jsx";
import { calculateImageCost, CostHelpModal } from "../utils/costCalculator";
import { loadModelSchema, calculateModelCost } from "../utils/modelSchemaLoader";

// Constants
const common_placeholder_text = 'Add any additional instructions, if any...'
const CREATION_OPTIONS = [
  {
    id: 'posing',
    label: 'Image to Video',
    desc: 'Create a video of the given image.',
    text_prompt_placeholder: common_placeholder_text,
    img: videoOptionAnimate,
    showReferenceImage: true,
    allowedModels: [
      'black-forest-labs/flux-kontext',
    ],
    allowedAspectRatios: ['Landscape', 'Portrait', 'Square'],
    showOutputSamples: false,
    showImageResolution: true,
    overrideInstagramPostType: 'reel',
  },
];

// Creation option defaults
const CREATION_DEFAULTS = {
  showReferenceImage: true,
  allowedModels: null, // null => allow all models (subject to other filters)
  allowedAspectRatios: ['Landscape', 'Portrait', 'Square'],
  showOutputSamples: true,
  showImageResolution: true,
  overrideInstagramPostType: 'reel',
};

// Allowed AI models for video generation
const ALLOWED_VIDEO_MODELS = ['google_veo-3.1-fast', 'minimax_hailuo-2.3-fast'];

// Aspect ratio icon component
const AspectIcon = ({ ratio, className = "w-3.5 h-3.5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.5">
    {ratio === 'Landscape' && <rect x="4" y="8" width="16" height="8" rx="2" ry="2" />}
    {ratio === 'Portrait' && <rect x="8" y="4" width="8" height="16" rx="2" ry="2" />}
    {ratio === 'Square' && <rect x="6" y="6" width="12" height="12" rx="2" ry="2" />}
  </svg>
);

const CreateVideoEditor = ({
  // State values
  instagramPostType,
  setInstagramPostType,
  creationInputMode,
  setCreationInputMode,
  selectedCreationOption,
  setSelectedCreationOption,
  message,
  setMessage,
  messageError,
  textboxUploadedImages,
  setTextboxUploadedImages,
  textboxUploadError,
  selectedModel,
  setSelectedModel,
  isModelCollapsed,
  setIsModelCollapsed,
  isOutputCollapsed,
  setIsOutputCollapsed,
  aspectRatio,
  setAspectRatio,
  reelDuration,
  setReelDuration,
  reelDurationError,
  resolution,
  setResolution,
  mode,
  setMode,
  imageResolution,
  setImageResolution,
  outputSamples,
  setOutputSamples,
  isAudioCollapsed,
  setIsAudioCollapsed,
  audioOption,
  setAudioOption,
  audioText,
  setAudioText,
  isGeneratingContent,
  generateContentLabel,
  isSidebarCollapsed,
  setIsSidebarCollapsed,
  isImageUploading,
  
  // Event handlers
  handleTextboxFileUpload,
  removeTextboxImage,
  handleStopGeneration,
  handleGenerateContent,
  
  // Refs
  messageTextareaRef,
  textboxFileInputRef,
  // New dresses props
  selectedDresses,
  setSelectedDresses
  ,
  // New human model props
  selectedHumanModel,
  setSelectedHumanModel,
// Event props
  selectedEvent,
  setSelectedEvent,
  // Product props
  productType,
  setProductType,
  productItem,
  setProductItem,
  // Style props
  selectedStyle,
  setSelectedStyle,
  // Real looks toggle
  realLooks,
  setRealLooks,
  setShowTour,
  // isCenteredPosition,
  disable1080p,
  disable720p,
  disablePremium,
  // AI Model props
  selectedAIModel,
  setSelectedAIModel
}) => {
  // Local state for dropdowns
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);
  
  // New chat UI state
  const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
  const chatInputRef = useRef(null);

  // AI Model cost calculation state
  const [currentModelSchema, setCurrentModelSchema] = useState(null);
  
  // Track previous AI model to detect model changes
  const prevAIModelRef = useRef(selectedAIModel);

  // Keep chat input in sync with persisted message (session)
  useEffect(() => {
    setChatInput(message || '');
  }, [message]);

  // Ensure selectedAIModel defaults to first allowed model if not set or invalid
  useEffect(() => {
    if (!selectedAIModel || !ALLOWED_VIDEO_MODELS.includes(selectedAIModel)) {
      setSelectedAIModel(ALLOWED_VIDEO_MODELS[0]);
    }
  }, [selectedAIModel, setSelectedAIModel]);

  // Load model schema when selectedAIModel changes
  useEffect(() => {
    const loadSchema = async () => {
      if (!selectedAIModel || selectedAIModel === 'auto') {
        setCurrentModelSchema(null);
        return;
      }

      try {
        const schema = await loadModelSchema(selectedAIModel);
        setCurrentModelSchema(schema);
      } catch (error) {
        console.error(`Failed to load schema for model ${selectedAIModel}:`, error);
        setCurrentModelSchema(null);
      }
    };

    loadSchema();
  }, [selectedAIModel]);

  const [isCreationModalOpen, setIsCreationModalOpen] = useState(false);
  
  // Refs for dropdowns
  const modelDropdownRef = useRef(null);
  // Removed creation dropdown; using chip selector instead
  
  // Pills modal state
  const [isRefModalOpen, setIsRefModalOpen] = useState(false);
  const [isTagProductModal1Open, setIsTagProductModal1Open] = useState(false);
  const [isAspectModalOpen, setIsAspectModalOpen] = useState(false);
  const [isQualityModalOpen, setIsQualityModalOpen] = useState(false);
  const [isSamplesModalOpen, setIsSamplesModalOpen] = useState(false);
  const [isResModalOpen, setIsResModalOpen] = useState(false);
  const [isModeModalOpen, setIsModeModalOpen] = useState(false);
  const [isDurModalOpen, setIsDurModalOpen] = useState(false);
  const modelHoverTimerRef = useRef(null);
  const [modelPillLeft, setModelPillLeft] = useState(0);
  const sidebarResizeObserverRef = useRef(null);
  const [isHumanModelModalOpen, setIsHumanModelModalOpen] = useState(false);

  useEffect(() => {
    const computeLeft = () => {
      try {
        const candidates = [];
        const nav = document.querySelector('nav.fixed');
        if (nav) {
          const rect = nav.getBoundingClientRect();
          candidates.push(rect.right);
        }
        const asides = Array.from(document.querySelectorAll('aside.fixed'));
        asides.forEach(a => {
          const rect = a.getBoundingClientRect();
          candidates.push(rect.right);
        });
        const maxRight = candidates.length ? Math.max(...candidates) : 0;
        const margin = 16; // px gap from sidebar
        setModelPillLeft(Math.max(12, Math.round(maxRight + margin)));
      } catch (_e) {
        setModelPillLeft(112); // safe fallback
      }
    };

    computeLeft();

    const onResize = () => computeLeft();
    window.addEventListener('resize', onResize);

    // Observe size changes of sidebars
    const ro = new ResizeObserver(() => computeLeft());
    sidebarResizeObserverRef.current = ro;
    const nav = document.querySelector('nav.fixed');
    if (nav) ro.observe(nav);
    document.querySelectorAll('aside.fixed').forEach(a => ro.observe(a));

    // Also observe class/style changes that might affect width
    const mo = new MutationObserver(() => computeLeft());
    mo.observe(document.body, { attributes: true, subtree: true, attributeFilter: ['class', 'style'] });

    return () => {
      window.removeEventListener('resize', onResize);
      if (sidebarResizeObserverRef.current) sidebarResizeObserverRef.current.disconnect();
      mo.disconnect();
    };
  }, [isSidebarCollapsed]);

  useEffect(() => {
    return () => {
      if (modelHoverTimerRef.current) clearTimeout(modelHoverTimerRef.current);
    };
  }, []);

  // Computed values
  const isFreeModel = selectedModel === 'free';
  const currentMediaType = instagramPostType === 'reel' ? 'video' : 'img';
  const placeholderText = currentMediaType === 'video'
    ? "e.g., A cinematic shot of a car driving down a rainy street at night"
    : "e.g., Create an image of a cozy cafe with warm lighting";
  const creationOptionError = !selectedCreationOption ? 'Please select a creation option.' : '';
  const resolvedCreationSelection = CREATION_OPTIONS.find(o => o.label === selectedCreationOption) || CREATION_OPTIONS.find(o => o.id === selectedCreationOption);
  const effectiveSelectedLabel = resolvedCreationSelection?.label || CREATION_OPTIONS[0]?.label;
  const primaryCreationChips = React.useMemo(() => {
    const selectedCreation = CREATION_OPTIONS.find(o => o.label === effectiveSelectedLabel);
    const base = CREATION_OPTIONS.slice(0, 1);
    if (selectedCreation && !base.some(o => o.id === selectedCreation.id)) {
      const others = CREATION_OPTIONS.filter(o => o.id !== selectedCreation.id);
      return [selectedCreation, ...others.slice(0, 0)];
    }
    return base;
  }, [effectiveSelectedLabel]);

  // Resolve creation option config based on currently selected label (parent stores label)
  const selectedCreationOptionDef = resolvedCreationSelection || CREATION_OPTIONS[0];
  const creationConfig = selectedCreationOptionDef || CREATION_DEFAULTS;
  const allowedAspectRatios = creationConfig.allowedAspectRatios || CREATION_DEFAULTS.allowedAspectRatios;
  const allowedAspectRatiosSet = new Set(allowedAspectRatios);

  // VIDEO-ONLY: Mask-related variables not used for video but kept for compatibility
  const hasMask = !!textboxUploadedImages[0]?.maskBlob;

  const showOutputSamples = (creationConfig.showOutputSamples !== false);

  const computedMessageError = messageError || '';
  
  // VIDEO-ONLY: Image-specific selection variables - NOT USED for video generation
  // These are commented out as they're not needed for video creation options
  // const dressSelected = Array.isArray(selectedDresses) && selectedDresses.length > 0;
  // const humanModelSelected = selectedHumanModel && !!selectedHumanModel.gender;
  // const productSelected = (productType && productItem);
  const productTaggingComplete = productType && productItem && textboxUploadedImages[0]?.ProductImgDtl?.view;

  // Calculate cost based on AI model if available
  // Cost updates when resolution, duration, or model changes (similar to TacoStudioEditor)
  const videoCost = React.useMemo(() => {
    // If we have a model schema and it's not 'auto', calculate model cost
    if (currentModelSchema && selectedAIModel && selectedAIModel !== 'auto') {
      // Build model parameters from available props
      // Note: resolution affects cost for non-Google models; Google models use generate_audio
      // Get default duration from first option in VideoDurationModal (6 for minimax_hailuo-2.3-fast, 4 for others)
      const defaultDuration = selectedAIModel === 'minimax_hailuo-2.3-fast' ? 6 : 4;
      const modelParams = {
        resolution: resolution || '720p', // Resolution affects pricing for most video models
        duration: reelDuration || defaultDuration,     // Duration multiplies the base cost
      };
      
      // Calculate cost using model schema
      // The calculateModelCost function uses resolution as priceFactor for non-Google models
      const modelCost = calculateModelCost(currentModelSchema, modelParams, 'video');
      
      // If model cost is available, use it
      if (modelCost > 0) {
        return modelCost;
      }
    }
    
    // Return 0 if no cost can be calculated
    return 0;
  }, [currentModelSchema, selectedAIModel, resolution, reelDuration]);

  // VIDEO-ONLY: Image-specific style checks - NOT USED for video
  // const HIDE_MODEL_PILL_STYLES = new Set(['Still Life', 'Invisible Mannequin', 'Flat Lay', 'Detail Shot', 'Hanging Shot']);
  // const shouldHideHumanModelPill = HIDE_MODEL_PILL_STYLES.has(selectedStyle);
  
  // VIDEO-ONLY: All video creation options use the same flow, no special hiding needed
  const shouldHideInputsForChangePose = false;
  const shouldHideInputsForChangeStyle = false;
  const shouldHideInputsForChangeBackground = false;
  const shouldHideInputsForRemoveBackground = false;

  // VIDEO-ONLY: Mask and real looks not used for video
  // useEffect(() => {
  //   if (hasMask && realLooks) {
  //     setRealLooks(false);
  //   }
  // }, [hasMask, realLooks, setRealLooks]);

  // Send from chatbox
  const handleSendChat = () => {
    handleValidatedGenerateContent();
  };

  // Handle validation before calling parent's handleGenerateContent
  const handleValidatedGenerateContent = () => {

    
    // VIDEO-ONLY: For video generation, we require a reference image (first frame)
    // All video creation options (posing, rampwalk, unbox) need an uploaded first frame
    if (textboxUploadedImages.length === 0) {
      toast.error("Please upload a Product photo.");
      setIsRefModalOpen(true);
      return;
    }

    // Require product tagging after reference image is uploaded
    if (textboxUploadedImages.length > 0 && !productTaggingComplete) {
      toast.error("Please add product tags (category, item, view & coverage).");
      setIsTagProductModal1Open(true);
      return;
    }

    // Resolve final message; do NOT fall back to placeholder
    const trimmedInput = (chatInput || '').trim();
    const isPlaceholderLike = trimmedInput === (creationConfig.text_prompt_placeholder || '').trim();

    if (!trimmedInput || isPlaceholderLike) {
      setMessage('');
    } else {
      setMessage(trimmedInput);
      setChatMessages(prev => [...prev, { role: 'user', content: trimmedInput, ts: Date.now() }]);
    }

    // Proceed with generation after state update tick (do not clear chat input)
    setTimeout(() => {
      handleGenerateContent();
    }, 0);
  };

  useEffect(() => {
    if (!showOutputSamples) {
      setOutputSamples(1);
    }
  }, [showOutputSamples, setOutputSamples]);

  // Ensure aspect ratio stays within allowed set for the selected creation option
  useEffect(() => {
    if (!allowedAspectRatiosSet.has(aspectRatio)) {
      setAspectRatio(allowedAspectRatios[0]);
    }
  }, [selectedCreationOption, allowedAspectRatios.join('|')]);

  // Auto-switch to 720p if 1080p is selected when switching to google_veo-3.1-fast (which doesn't support 1080p)
  useEffect(() => {
    if (selectedAIModel === 'google_veo-3.1-fast' && resolution === '1080p') {
      setResolution('720p');
    }
    // If switching away from minimax_hailuo-2.3-fast and resolution is 768p, switch to 720p (768p only available for minimax)
    if (selectedAIModel !== 'minimax_hailuo-2.3-fast' && resolution === '768p') {
      setResolution('720p');
    }
  }, [selectedAIModel, resolution, setResolution]);

  // Set default duration when AI model changes
  useEffect(() => {
    // Only update duration if the model actually changed (not on initial mount)
    if (prevAIModelRef.current && prevAIModelRef.current !== selectedAIModel) {
      if (selectedAIModel === 'minimax_hailuo-2.3-fast') {
        // For minimax: default duration is 6s (valid options: [6, 10])
        setReelDuration(6);
      } else {
        // For other models (e.g., google_veo-3.1-fast): default duration is 4s (valid options: [4, 6, 8])
        setReelDuration(4);
      }
    }
    // Update the ref to track the current model
    prevAIModelRef.current = selectedAIModel;
  }, [selectedAIModel, setReelDuration]);

  // For minimax_hailuo-2.3-fast: Handle resolution/duration restrictions
  useEffect(() => {
    if (selectedAIModel === 'minimax_hailuo-2.3-fast') {
      // If resolution is 720p (not available for minimax), switch to 768p (default)
      if (resolution === '720p') {
        setResolution('768p');
      }
      // If duration is 10s and resolution is not 768p, switch to 768p
      if (reelDuration === 10 && resolution !== '768p') {
        setResolution('768p');
      }
      // If resolution is 1080p and duration is 10s, switch to 6s (1080p only supports 6s)
      if (resolution === '1080p' && reelDuration === 10) {
        setReelDuration(6);
      }
    } else {
      // If switching away from minimax and duration is 10s (not available for other models), reset to 4s (default for other models)
      if (reelDuration === 10) {
        setReelDuration(4);
      }
    }
  }, [selectedAIModel, reelDuration, resolution, setResolution, setReelDuration]);


  // Close dropdowns when clicking outside
  useEffect(() => {
    const onDocClick = (e) => {
      if (modelDropdownRef.current && !modelDropdownRef.current.contains(e.target)) {
        setIsModelDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Ensure a default creation option is selected before paint (mandatory field)
  useLayoutEffect(() => {
  }, [selectedCreationOption, setSelectedCreationOption]);

  // Always set Instagram post type; allow creation config to override (defaults to 'reel')
  useEffect(() => {
    const desiredPostType = creationConfig.overrideInstagramPostType || 'reel';
    setInstagramPostType(desiredPostType);
  }, [setInstagramPostType, selectedCreationOption]);

  return (
    <>
      {/* Global bottom chatbox - Always positioned at bottom */} 
      <div className="fixed bottom-0 left-0 right-0 z-50 px-2 sm:px-4 lg:px-6 pb-2 sm:pb-4 lg:pb-6">
        <div className="max-w-3xl mx-auto">
          <div className="rounded-xl border border-white/10 bg-[#171717] shadow-2xl backdrop-blur-sm">
            
            {/* Top row with Creation Selector and Model Pill - Inside textarea container */}
            <div className="flex items-center justify-between gap-1.5 px-2 py-1.5 border-b border-white/5">
              {/* Creation Selector - Primary position */}
              <div className="relative group">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-full bg-[#252525] border border-white/10 px-2 py-1 text-[10px] sm:text-xs hover:bg-[#2a2a2a] hover:border-white/20 transition-all"
                  onClick={() => setIsCreationModalOpen(true)}
                  data-tour="creation-option"
                  title="Click to see more options..."
                >
                  <span className="text-xs">
                    {(() => {
                      switch(resolvedCreationSelection?.id) {
                        case 'create_image': return '👗';
                        case 'change_pose': return '💃';
                        // case 'add_logo': return '🏷️';
                        case 'remove_background': return '✂️';
                        case 'change_background': return '🌟';
                        case 'change_style': return '🎨';
                        // case 'combine_multiple_images': return '🔄';
                        // case 'create_poster': return '📋';
                        default: return '✨';
                      }
                    })()}
                  </span>
                  <span className="text-white font-medium truncate max-w-[150px] sm:max-w-[140px]">{effectiveSelectedLabel}</span>
                  <FaChevronDown className="text-gray-400 text-[10px]" />
                </button>
                
                {/* Tooltip on hover */}
                <div className="absolute hidden group-hover:block bottom-full left-0 mb-1 pointer-events-none z-50">
                  <div className="rounded-lg border border-white/10 bg-[#111111]/95 backdrop-blur-2xl px-2.5 py-1.5 shadow-2xl max-w-[16rem] text-[10px] leading-snug text-white relative">
                    <div className="font-medium mb-0.5">{resolvedCreationSelection?.label}</div>
                    <div className="text-gray-300">{resolvedCreationSelection?.desc}</div>
                    <div className="absolute left-3 -bottom-0.5 w-1.5 h-1.5 bg-[#111111]/95 border border-white/10 rotate-45"></div>
                  </div>
                </div>
              </div>

              {/* Right-side container: Model pill + Cost */}
              <div className="flex items-center gap-1.5">
                {/* Cost pill with help */}
                {/* <div className="inline-flex items-center gap-1 rounded-full bg-[#0f0f0f] text-gray-300 border border-white/10 px-2 py-1 text-[10px]" title="Estimated cost">
                  <span className="opacity-75">Cost</span>
                  <span className="font-semibold">₹{imgCost.toFixed(2)}</span>
                  <button type="button" onClick={() => setIsCostHelpOpen(true)} className="ml-1 text-gray-400 hover:text-white" aria-label="Cost help" title="What affects cost?">
                    <FaQuestionCircle className="w-3.5 h-3.5" />
                  </button>
                </div> */}

                {/* AI Model Selector - Allows selection between multiple video models */}
                <div className="flex-shrink-0 min-w-0 max-w-full sm:max-w-none"> 
                  <AIModelSelector
                    selectedModelId={selectedAIModel || ALLOWED_VIDEO_MODELS[0]}
                    onModelSelect={setSelectedAIModel}
                    contentType="video"
                    artFormat="video"
                    allowedModelIds={ALLOWED_VIDEO_MODELS}
                  />
                </div>
              </div>
            </div>

            {/* Error message */}
            {creationOptionError && (
              <div className="px-2 py-1 text-[10px] text-red-400 flex items-center gap-1 border-b border-white/5">
                <span>⚠️</span>
                {creationOptionError}
              </div>
            )}

            {/* Main textarea and generate button */}
            <div className="p-1.5 sm:p-2">
              <div className="flex items-center gap-1.5">
                <textarea
                  ref={(el) => { chatInputRef.current = el; if (messageTextareaRef) { messageTextareaRef.current = el; } }}
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder={creationConfig.text_prompt_placeholder}
                  className="w-full bg-transparent border-0 rounded-lg px-2.5 py-2 text-xs sm:text-sm text-gray-200 focus:outline-none focus:ring-0 resize-none placeholder:text-gray-500 placeholder:italic placeholder:opacity-70"
                  style={{ minHeight: '16px', maxHeight: '80px' }}
                  readOnly={shouldHideInputsForRemoveBackground}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendChat();
                    }
                  }}
                  onFocus={(e) => {
                    const current = (chatInput || '').trim();
                    const ph = (creationConfig.text_prompt_placeholder || '').trim();
                    if (current && current === ph) {
                      try { e.target.select(); } catch (_) {}
                      setChatInput('');
                    }
                  }}
                />
                {/* Reference image thumbnail */}
                {textboxUploadedImages.length > 0 && (
                  <div className="shrink-0 w-12 h-12 rounded-lg overflow-hidden border border-white/10">
                    <img
                      src={textboxUploadedImages[0].preview || textboxUploadedImages[0].url}
                      alt="Reference"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                {/* Image upload loading indicator */}
                {isImageUploading && (
                  <div className="shrink-0 w-12 h-12 rounded-lg border border-white/10 bg-[#0f0f0f] flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-white/20 border-t-white/80"></div>
                  </div>
                )}
                <button
                  type="button"
                  disabled={isGeneratingContent}
                  onClick={handleSendChat}
                  data-tour="generate-button"
                  className={`shrink-0 inline-flex items-center gap-1.5 rounded-lg px-3 sm:px-4 py-2 sm:py-2.5 text-xs font-semibold transition-all duration-300 shadow-lg ${
                    isGeneratingContent 
                      ? 'bg-gray-600 text-white/80 cursor-not-allowed animate-pulse' 
                      : 'bg-white text-black hover:opacity-90'
                  }`}
                  title="Generate"
                >
                  {isGeneratingContent ? (
                    <FaSpinner className="text-xs animate-spin" />
                  ) : (
                    <FaMagic className="text-xs" />
                  )}
                  <span className="flex flex-col items-start">
                    <span>
                      {isGeneratingContent ? 'Generating...' : 'Generate'}
                    </span>
                    {!isGeneratingContent && videoCost > 0 && (
                      <span className="text-[9px] font-normal opacity-75 leading-tight">
                        ₹{videoCost.toFixed(2)} incl. GST
                      </span>
                    )}
                  </span>
                </button>
              </div>

              {/* Settings pills row */}
              <div className="flex flex-wrap items-center gap-1 mt-1.5">
                {/* Start frame pill */}
                {creationConfig.showReferenceImage !== false && (
                  <div className="relative group">
                    <button
                      type="button"
                      className={`inline-flex items-center gap-0.5 rounded-full border px-1.5 py-1 text-[20px] ${
                        isImageUploading 
                          ? 'bg-[#0a0a0a] text-gray-500 border-white/5 cursor-not-allowed opacity-50' 
                          : 'bg-[#0f0f0f] text-gray-200 border-white/10 hover:bg-white/10'
                      }`}
                      onClick={() => !isImageUploading && setIsRefModalOpen(true)}
                      disabled={isImageUploading}
                      data-tour="upload-section"
                      title={isImageUploading ? "Uploading image..." : "Reference image"}
                      aria-describedby="ref-tooltip"
                    >
                      {isImageUploading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/20 border-t-white/80"></div>
                      ) : (
                        <>
                          <FaPaperclip className="text-blue-300/90 text-[20px]" />
                          {textboxUploadedImages.length > 0 && (
                            <span className="ml-0.5 text-gray-400 text-[20px]">✓</span>
                          )}
                        </>
                      )}
                    </button>
                    <div
                      id="ref-tooltip"
                      role="tooltip"
                      className="absolute hidden group-hover:block group-focus-within:block bottom-full left-0 mb-1 pointer-events-none"
                    >
                      <div className="rounded-lg border border-white/10 bg-[#111111]/95 backdrop-blur-2xl px-2 py-1 shadow-2xl max-w-[18rem] text-[10px] leading-snug text-white relative">
                        <div>{isImageUploading ? "Uploading image..." : "Upload product photo"}</div>
                        <div className="absolute left-2 -bottom-0.5 w-1 h-1 bg-[#111111]/95 border border-white/10 rotate-45"></div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tag Product1 pill - Detailed product tagging */}
                <div className="relative group">
                  <button
                    type="button"
                    className="inline-flex items-center gap-0.5 rounded-full bg-[#0f0f0f] text-gray-200 border border-white/10 px-1.5 py-1 text-[10px] hover:bg-white/10"
                    onClick={() => setIsTagProductModal1Open(true)}
                    title="Detailed product tagging"
                    aria-describedby="tag-product1-tooltip"
                  >
                    <FaTshirt className="text-purple-300/90 text-[20px]" />
                    {productTaggingComplete && (
                      <span className="ml-0.5 text-gray-400 text-[20px]">✓</span>
                    )}
                  </button>
                  <div
                    id="tag-product1-tooltip"
                    role="tooltip"
                    className="absolute hidden group-hover:block group-focus-within:block bottom-full left-0 mb-1 pointer-events-none"
                  >
                    <div className="rounded-lg border border-white/10 bg-[#111111]/95 backdrop-blur-2xl px-2 py-1 shadow-2xl max-w-[18rem] text-[10px] leading-snug text-white relative">
                      <div>Tag category, item, view & coverage</div>
                      <div className="absolute left-2 -bottom-0.5 w-1 h-1 bg-[#111111]/95 border border-white/10 rotate-45"></div>
                    </div>
                  </div>
                </div>

                {/* Mode pill (Video only) */}
                {/* {instagramPostType === 'reel' && (
                  <div className="relative group">
                    <button
                      type="button"
                      className="inline-flex items-center gap-0.5 rounded-full bg-[#0f0f0f] text-gray-200 border border-white/10 px-1.5 py-1 text-[10px] hover:bg-white/10"
                      onClick={() => setIsModeModalOpen(true)}
                      data-tour="video-mode"
                      title="Video mode"
                      aria-describedby="mode-tooltip"
                    >
                      <span className="text-blue-300/90 text-[18px]">✨</span>
                      <span>{mode === 'pro_max' ? 'Premium' : 'Standard'}</span>
                    </button>
                    <div
                      id="mode-tooltip"
                      role="tooltip"
                      className="absolute hidden group-hover:block group-focus-within:block bottom-full left-0 mb-1 pointer-events-none"
                    >
                      <div className="rounded-lg border border-white/10 bg-[#111111]/95 backdrop-blur-2xl px-2 py-1 shadow-2xl max-w-[18rem] text-[10px] leading-snug text-white relative">
                        <div>Set video mode</div>
                        <div className="text-gray-300">Current: {mode === 'pro_max' ? 'Pro Max' : 'Pro'}</div>
                        <div className="absolute left-2 -bottom-0.5 w-1 h-1 bg-[#111111]/95 border border-white/10 rotate-45"></div>
                      </div>
                    </div>
                  </div>
                )} */}

                {/* Resolution pill (Video only) */}
                {instagramPostType === 'reel' && (
                  <div className="relative group">
                    <button
                      type="button"
                      className={`inline-flex items-center gap-0.5 rounded-full border px-1.5 py-1 text-[10px] ${
                        (selectedAIModel === 'google_veo-3.1-fast' && resolution === '1080p') ||
                        (selectedAIModel === 'minimax_hailuo-2.3-fast' && resolution === '1080p' && reelDuration === 10)
                          ? 'bg-amber-900/20 border-amber-500/30 text-amber-300/80'
                          : 'bg-[#0f0f0f] text-gray-200 border-white/10 hover:bg-white/10'
                      }`}
                      onClick={() => setIsResModalOpen(true)}
                      data-tour="video-resolution"
                      title="Video resolution"
                      aria-describedby="resolution-tooltip"
                    >
                      <FaCog ratio={aspectRatio} className="text-blue-300/90 text-[18px]" />
                      <span>{resolution === '1080p' ? '1080p' : (resolution === '768p' ? '768p' : '720p')}</span>
                    </button>
                    <div
                      id="resolution-tooltip"
                      role="tooltip"
                      className="absolute hidden group-hover:block group-focus-within:block bottom-full left-0 mb-1 pointer-events-none z-50"
                    >
                      <div className="rounded-lg border border-white/10 bg-[#111111]/95 backdrop-blur-2xl px-2 py-1 shadow-2xl max-w-[18rem] text-[10px] leading-snug text-white relative">
                        <div>Set video resolution</div>
                        <div className="text-gray-300">Current: {resolution === '1080p' ? '1080p' : (resolution === '768p' ? '768p' : '720p')}</div>
                        {selectedAIModel === 'google_veo-3.1-fast' && (
                          <div className="text-amber-300/90 mt-1 text-[9px]">
                            ⚠️ 1080p not available for google_veo-3.1-fast. Use google_veo-3.1 for 1080p support.
                          </div>
                        )}
                        {selectedAIModel === 'minimax_hailuo-2.3-fast' && reelDuration === 10 && resolution === '1080p' && (
                          <div className="text-amber-300/90 mt-1 text-[9px]">
                            ⚠️ 1080p not available for 10s duration. 10 seconds is only available for 768p resolution.
                          </div>
                        )}
                        <div className="absolute left-2 -bottom-0.5 w-1 h-1 bg-[#111111]/95 border border-white/10 rotate-45"></div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Duration pill (Video only) */}
                {instagramPostType === 'reel' && (
                  <div className="relative group">
                    <button
                      type="button"
                      className="inline-flex items-center gap-0.5 rounded-full bg-[#0f0f0f] text-gray-200 border border-white/10 px-1.5 py-1 text-[10px] hover:bg-white/10"
                      onClick={() => setIsDurModalOpen(true)}
                      data-tour="video-duration"
                      title="Video duration"
                      aria-describedby="duration-tooltip"
                    >
                      <FaStopwatch ratio={aspectRatio} className="text-blue-300/90 text-[18px]" />
                      <span>{(reelDuration || 4)}s</span>
                    </button>
                    <div
                      id="duration-tooltip"
                      role="tooltip"
                      className="absolute hidden group-hover:block group-focus-within:block bottom-full left-0 mb-1 pointer-events-none"
                    >
                      <div className="rounded-lg border border-white/10 bg-[#111111]/95 backdrop-blur-2xl px-2 py-1 shadow-2xl max-w-[18rem] text-[10px] leading-snug text-white relative">
                        <div>Set video duration</div>
                        <div className="text-gray-300">Current: {(reelDuration || 4)}s</div>
                        <div className="absolute left-2 -bottom-0.5 w-1 h-1 bg-[#111111]/95 border border-white/10 rotate-45"></div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Aspect Ratio pill */}
                {/* {!shouldHideInputsForChangeStyle && !shouldHideInputsForChangePose && !shouldHideInputsForRemoveBackground && !shouldHideInputsForChangeBackground && (<div className="relative group">
                  <button
                    type="button"
                    className={`inline-flex items-center gap-0.5 rounded-full border px-1.5 py-1 text-[10px] ${
                      mode === 'pro' 
                        ? 'bg-[#0a0a0a] text-gray-500 border-white/5 cursor-not-allowed opacity-50' 
                        : 'bg-[#0f0f0f] text-gray-200 border-white/10 hover:bg-white/10'
                    }`}
                    onClick={() => mode !== 'pro' && setIsAspectModalOpen(true)}
                    disabled={mode === 'pro'}
                    data-tour="aspect-ratio"
                    title={mode === 'pro' ? 'Aspect ratio disabled in Pro mode' : 'Aspect Ratio'}
                    aria-describedby="aspect-tooltip"
                  >
                    <AspectIcon ratio={aspectRatio} className={`w-6 h-6 ${mode === 'pro' ? 'text-gray-500' : 'text-blue-300/90'}`} />
                    <span>{aspectRatio === 'Landscape' ? '16:9' : (aspectRatio === 'Portrait' ? '9:16' : '1:1')}</span>
                  </button>
                  <div
                    id="aspect-tooltip"
                    role="tooltip"
                    className="absolute hidden group-hover:block group-focus-within:block bottom-full left-0 mb-1 pointer-events-none"
                  >
                    <div className="rounded-lg border border-white/10 bg-[#111111]/95 backdrop-blur-2xl px-2 py-1 shadow-2xl max-w-[18rem] text-[10px] leading-snug text-white relative">
                      <div>{mode === 'pro' ? 'Aspect ratio disabled' : 'Set aspect ratio'}</div>
                      <div className="text-gray-300">
                        {mode === 'pro' 
                          ? 'Not available in Pro mode' 
                          : `Current: ${aspectRatio === 'Landscape' ? '16:9' : (aspectRatio === 'Portrait' ? '9:16' : '1:1')}`
                        }
                      </div>
                      <div className="absolute left-2 -bottom-0.5 w-1 h-1 bg-[#111111]/95 border border-white/10 rotate-45"></div>
                    </div>
                  </div>
                </div>)} */}

                {/* Output Samples pill */}
                {showOutputSamples && (
                  <div className="relative group">
                    <button
                      type="button"
                      className="inline-flex items-center gap-0.5 rounded-full bg-[#0f0f0f] text-gray-200 border border-white/10 px-1.5 py-1 text-[10px] hover:bg-white/10"
                      onClick={() => setIsSamplesModalOpen(true)}
                      title="Output samples"
                      aria-describedby="samples-tooltip"
                    >
                      <span>{outputSamples}×</span>
                    </button>
                    <div
                      id="samples-tooltip"
                      role="tooltip"
                      className="absolute hidden group-hover:block group-focus-within:block bottom-full left-0 mb-1 pointer-events-none"
                    >
                      <div className="rounded-lg border border-white/10 bg-[#111111]/95 backdrop-blur-2xl px-2 py-1 shadow-2xl max-w-[18rem] text-[10px] leading-snug text-white relative">
                        <div>Number of outputs</div>
                        <div className="text-gray-300">{outputSamples ? `Current: ${outputSamples}` : ''}</div>
                        <div className="absolute left-2 -bottom-0.5 w-1 h-1 bg-[#111111]/95 border border-white/10 rotate-45"></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
                </div>
      </div>

    {/* Creation options modal */}
    {isCreationModalOpen && (
      <VideoOptionsModal
        isOpen={isCreationModalOpen}
        onClose={() => setIsCreationModalOpen(false)}
        options={CREATION_OPTIONS}
        selectedLabel={effectiveSelectedLabel}
        onSelect={(labelOrId) => { const opt = CREATION_OPTIONS.find(o => o.label === labelOrId) || CREATION_OPTIONS.find(o => o.id === labelOrId); if (opt) { setSelectedCreationOption(opt.id); } setIsCreationModalOpen(false); }}
      />
    )}
    
    {/* Setting modals mounted */}
    {isRefModalOpen && (
      <ReferenceImageModal
        isOpen={isRefModalOpen}
        onClose={() => setIsRefModalOpen(false)}
        textboxUploadedImages={textboxUploadedImages}
        textboxUploadError={textboxUploadError}
        handleTextboxFileUpload={handleTextboxFileUpload}
        removeTextboxImage={removeTextboxImage}
        textboxFileInputRef={textboxFileInputRef}
        setTextboxUploadedImages={setTextboxUploadedImages}
        disableMasking={shouldHideInputsForRemoveBackground}
        // Product selection props
        productType={productType}
        setProductType={setProductType}
        productItem={productItem}
        setProductItem={setProductItem}
        setSelectedDresses={setSelectedDresses}
        // Instagram post type (to hide mask buttons for video)
        instagramPostType={instagramPostType}
      />
    )}
    {isTagProductModal1Open && (
      <TagProductModal1
        isOpen={isTagProductModal1Open}
        onClose={() => setIsTagProductModal1Open(false)}
        productType={productType}
        setProductType={setProductType}
        productItem={productItem}
        setProductItem={setProductItem}
        textboxUploadedImages={textboxUploadedImages}
        setTextboxUploadedImages={setTextboxUploadedImages}
      />
    )}
    {isAspectModalOpen && (
      <AspectRatioModal
        isOpen={isAspectModalOpen}
        onClose={() => setIsAspectModalOpen(false)}
        aspectRatio={aspectRatio}
        setAspectRatio={setAspectRatio}
        allowedAspectRatiosSet={allowedAspectRatiosSet}
        AspectIcon={AspectIcon}
      />
    )}
    {isSamplesModalOpen && (
      <OutputSamplesModal
        isOpen={isSamplesModalOpen}
        onClose={() => setIsSamplesModalOpen(false)}
        outputSamples={outputSamples}
        setOutputSamples={setOutputSamples}
        isFreeModel={isFreeModel}
      />
    )}
    {isResModalOpen && (
      <VideoResolutionModal
        isOpen={isResModalOpen}
        onClose={() => setIsResModalOpen(false)}
        resolution={resolution}
        setResolution={setResolution}
        isFreeModel={isFreeModel}
        mode={mode}
        disable1080p={disable1080p}
        disable720p={disable720p}
        selectedAIModel={selectedAIModel}
        reelDuration={reelDuration}
      />
    )}
    {isModeModalOpen && (
      <VideoModeModal
        isOpen={isModeModalOpen}
        onClose={() => setIsModeModalOpen(false)}
        mode={mode}
        setMode={setMode}
        disablePremium={disablePremium}
      />
    )}
    {isDurModalOpen && (
      <VideoDurationModal
        isOpen={isDurModalOpen}
        onClose={() => setIsDurModalOpen(false)}
        reelDuration={reelDuration}
        setReelDuration={setReelDuration}
        reelDurationError={reelDurationError}
        isFreeModel={isFreeModel}
        selectedAIModel={selectedAIModel}
        resolution={resolution}
        setResolution={setResolution}
      />
    )}

    </>
  );
};

export default CreateVideoEditor; 